/**
 * Email Domain Configuration
 * 
 * Configures allowed email domains for each user role.
 * Teachers and Admins use Magic Link authentication just like students.
 */

import { USER_ROLES } from './userRoles';

// Domain whitelist per role
export const ROLE_DOMAINS = {
  // Admin domains - only specific addresses can be admin
  [USER_ROLES.ADMIN]: [
    'haw-hamburg.de'  // Admin must also be from HAW
  ],
  
  // Teacher domains
  [USER_ROLES.TEACHER]: [
    'haw-hamburg.de'  // Teachers must be from HAW
  ],
  
  // Student domains
  [USER_ROLES.STUDENT]: [
    'haw-hamburg.de'  // Students must be from HAW
  ]
};

// Pre-configured admin emails (these users get admin role automatically)
export const ADMIN_EMAILS = [
  // Add admin email addresses here
  // 'admin@haw-hamburg.de'
];

// Pre-configured teacher emails (these users get teacher role automatically)
// Note: New teachers can also be promoted by admins
export const TEACHER_EMAILS = [
  // Add teacher email addresses here
  // 'professor@haw-hamburg.de'
];

/**
 * Check if email is allowed for a specific role
 * @param {string} email - Email address
 * @param {string} role - Target role
 * @returns {boolean}
 */
export function isEmailAllowedForRole(email, role) {
  if (!email || typeof email !== 'string') return false;
  if (!email.includes('@')) return false;
  
  const domain = email.split('@')[1]?.toLowerCase();
  const allowedDomains = ROLE_DOMAINS[role];
  
  if (!allowedDomains) return false;
  return allowedDomains.includes(domain);
}

/**
 * Determine role based on email address
 * @param {string} email - Email address
 * @returns {string|null} - Suggested role or null if email not allowed
 */
export function determineRoleFromEmail(email) {
  if (!email) return null;
  
  const normalizedEmail = email.toLowerCase().trim();
  
  // Check if it's a pre-configured admin
  if (ADMIN_EMAILS.includes(normalizedEmail)) {
    return USER_ROLES.ADMIN;
  }
  
  // Check if it's a pre-configured teacher
  if (TEACHER_EMAILS.includes(normalizedEmail)) {
    return USER_ROLES.TEACHER;
  }
  
  // Default to student for valid HAW emails
  if (isEmailAllowedForRole(normalizedEmail, USER_ROLES.STUDENT)) {
    return USER_ROLES.STUDENT;
  }
  
  return null;
}

/**
 * Check if any domain is allowed for the email
 * @param {string} email - Email address
 * @returns {boolean}
 */
export function isEmailAllowed(email) {
  if (!email || typeof email !== 'string') return false;
  if (!email.includes('@')) return false;
  
  const domain = email.split('@')[1]?.toLowerCase();
  
  // Check all role domains
  return Object.values(ROLE_DOMAINS)
    .flat()
    .includes(domain);
}

/**
 * Get error message for invalid email
 * @param {string} email - Email address
 * @returns {string}
 */
export function getEmailDomainError(email) {
  if (!email || !email.includes('@')) {
    return 'Bitte gib eine gültige E-Mail-Adresse ein.';
  }
  
  const domain = email.split('@')[1];
  const allDomains = [...new Set(Object.values(ROLE_DOMAINS).flat())];
  const domainsText = allDomains.map(d => `@${d}`).join(', ');
  
  return `Die Domain @${domain} ist nicht erlaubt. Bitte verwende eine Hochschul-E-Mail-Adresse (${domainsText}).`;
}

/**
 * Get allowed domains display
 * @returns {string[]}
 */
export function getAllowedDomainsDisplay() {
  const allDomains = [...new Set(Object.values(ROLE_DOMAINS).flat())];
  return allDomains.map(d => `@${d}`);
}

export default {
  ROLE_DOMAINS,
  ADMIN_EMAILS,
  TEACHER_EMAILS,
  isEmailAllowedForRole,
  determineRoleFromEmail,
  isEmailAllowed,
  getEmailDomainError,
  getAllowedDomainsDisplay
};
